import React from 'react'
import {
    Box, Checkbox, Chip, CircularProgress, InputAdornment, List, ListItem, ListItemButton,
    ListItemIcon, ListItemText, Pagination, TextField, Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { ApiPushSearchUsers } from '../../apiService/Core/Apicall'
import { checkboxSx, mutedText, searchFieldStyle } from './pushStyles'
import { getErrorMessage, qs, useDebouncedValue } from './pushUtils'
import type { UserLite } from './types'

interface Props {
    selected: UserLite[]
    onChange: (users: UserLite[]) => void
    /** Maximum number of selectable users. */
    max?: number
    /** Users that cannot be picked (e.g. already members). */
    excludeIds?: string[]
    placeholder?: string
}

const PAGE_SIZE = 8
const CHIP_LIMIT = 50

/** Debounced name / email / user id search with multi-select. */
const UserPicker: React.FC<Props> = ({ selected, onChange, max, excludeIds = [], placeholder }) => {
    const [text, setText] = React.useState('')
    // page is tied to the query it was chosen for, so a new query always starts at page 1
    const [pageState, setPageState] = React.useState({ q: '', page: 1 })
    const [items, setItems] = React.useState<UserLite[]>([])
    const [totalPages, setTotalPages] = React.useState(1)
    const [loading, setLoading] = React.useState(false)
    const [error, setError] = React.useState<string | null>(null)
    const reqRef = React.useRef(0)
    const invalidate = React.useCallback(() => { reqRef.current += 1 }, [])
    const q = useDebouncedValue(text.trim())
    const page = pageState.q === q ? pageState.page : 1

    React.useEffect(() => {
        const requestId = ++reqRef.current
        if (q.length < 2) {
            setItems([])
            setError(null)
            setLoading(false)
            return
        }
        setLoading(true)
        ApiPushSearchUsers(qs({ q, page, limit: PAGE_SIZE }), { silent: true })
            .then((res) => {
                if (requestId !== reqRef.current) return
                setItems(res?.data?.items ?? [])
                setTotalPages(res?.meta?.pagination?.totalPages || 1)
                setError(null)
            })
            .catch((e) => {
                if (requestId !== reqRef.current) return
                setItems([])
                setError(getErrorMessage(e))
            })
            .finally(() => {
                if (requestId === reqRef.current) setLoading(false)
            })
        return invalidate
    }, [q, page, invalidate])

    const selectedIds = new Set(selected.map((u) => u.id))
    const excluded = new Set(excludeIds)
    const atMax = typeof max === 'number' && selected.length >= max

    const toggle = (u: UserLite) => {
        if (selectedIds.has(u.id)) onChange(selected.filter((s) => s.id !== u.id))
        else if (!atMax) onChange([...selected, { id: u.id, name: u.name, email: u.email }])
    }

    return (
        <Box>
            <TextField
                fullWidth
                size="small"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={placeholder ?? 'Search by name, email or user ID (min 2 characters)'}
                sx={searchFieldStyle}
                slotProps={{
                    input: {
                        startAdornment: (<InputAdornment position="start"><SearchIcon className="color-primary" /></InputAdornment>),
                        endAdornment: loading ? <CircularProgress size={16} sx={{ color: '#ff6a3d' }} /> : null,
                    },
                }}
            />
            {selected.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5, alignItems: 'center' }}>
                    {selected.slice(0, CHIP_LIMIT).map((u) => (
                        <Chip
                            key={u.id}
                            size="small"
                            label={u.name || u.email || u.id}
                            title={u.email}
                            onDelete={() => onChange(selected.filter((s) => s.id !== u.id))}
                            sx={{ background: '#241815', color: '#e0e0e0', border: '1px solid #ff6a3d40', '& .MuiChip-deleteIcon': { color: '#ff6a3d' } }}
                        />
                    ))}
                    {selected.length > CHIP_LIMIT && (
                        <Typography sx={mutedText}>+{selected.length - CHIP_LIMIT} more</Typography>
                    )}
                    <Typography
                        onClick={() => onChange([])}
                        sx={{ fontSize: 11, color: '#ff6a3d', cursor: 'pointer', ml: 0.5, '&:hover': { textDecoration: 'underline' } }}
                    >
                        Clear all
                    </Typography>
                </Box>
            )}
            {typeof max === 'number' && (
                <Typography sx={{ ...mutedText, fontSize: 11, mt: 0.5 }}>{selected.length} / {max} selected</Typography>
            )}
            {q.length >= 2 && (
                <Box sx={{ mt: 1, border: '1px solid #333', borderRadius: '10px', background: '#1a1a1a' }}>
                    {error ? (
                        <Typography sx={{ p: 2, fontSize: 13, color: '#ef4444' }}>{error}</Typography>
                    ) : items.length === 0 && !loading ? (
                        <Typography sx={{ p: 2, fontSize: 13, color: '#6a6a6a' }}>No users found.</Typography>
                    ) : (
                        <List dense disablePadding>
                            {items.map((u) => {
                                const isSel = selectedIds.has(u.id)
                                const isExcluded = excluded.has(u.id)
                                const disabled = isExcluded || (!isSel && atMax)
                                return (
                                    <ListItem key={u.id} disablePadding>
                                        <ListItemButton disabled={disabled} onClick={() => toggle(u)} sx={{ '&:hover': { background: '#ff6a3d08' } }}>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <Checkbox edge="start" checked={isSel || isExcluded} disableRipple sx={checkboxSx} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={u.name || '-'}
                                                secondary={`${u.email ?? '-'}${isExcluded ? ' (already added)' : ''}`}
                                                slotProps={{
                                                    primary: { sx: { color: '#e0e0e0', fontSize: 13, fontWeight: 600 } },
                                                    secondary: { sx: { color: '#6a6a6a', fontSize: 11 } },
                                                }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                )
                            })}
                        </List>
                    )}
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                            <Pagination
                                size="small"
                                count={totalPages}
                                page={page}
                                onChange={(_, p) => setPageState({ q, page: p })}
                                sx={{ '& .MuiPaginationItem-root': { color: '#898989' }, '& .Mui-selected': { background: '#ff6a3d30 !important', color: '#ff6a3d' } }}
                            />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    )
}

export default UserPicker
